import type { MarketplaceService } from '../../types/pricing';

export interface NamedAddonDefinition {
  key: string;
  name: string;
  description?: string;
  price_cents: number;
  group?: string;
  allowed_variant_keys?: string[];
  requires_any_variant_keys?: string[];
  requires_any_recurring_bolt_on_keys?: string[];
  conflicts_recurring_bolt_on_keys?: string[];
  requirement_description?: string;
  type?: string;
}

export interface OneTimeAddonDefinition extends NamedAddonDefinition {
  max_per_order?: number;
}

export interface NamedAddonSelection {
  addon_key: string;
}

export interface OneTimeAddonSelection extends NamedAddonSelection {
  quantity: number;
}

export interface ResolvedNamedAddonSelection extends NamedAddonSelection {
  name: string;
  description?: string;
  group?: string;
  type?: string;
  price_cents: number;
  total_price_cents: number;
}

export interface ResolvedOneTimeAddonSelection extends OneTimeAddonSelection {
  name: string;
  description?: string;
  group?: string;
  type?: string;
  price_cents: number;
  total_price_cents: number;
  max_per_order: number;
}

export interface ServiceConfigurationPayload {
  additional_users?: number;
  recurring_bolt_ons?: NamedAddonSelection[];
  one_time_addons?: OneTimeAddonSelection[];
}

export function clampConfiguredAddonQuantity(value: number, max = Number.MAX_SAFE_INTEGER): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(Math.floor(value), max));
}

function normalizeStringArray(value: any): string[] | undefined {
  const normalized = Array.isArray(value)
    ? value.map((entry: any) => String(entry || '').trim()).filter(Boolean)
    : undefined;

  return normalized?.length ? normalized : undefined;
}

function getNamedAddonDefinitions(rawDefinitions: any, defaultType: string): NamedAddonDefinition[] {
  if (!Array.isArray(rawDefinitions)) return [];

  return rawDefinitions
    .map((entry: any) => {
      const key = String(entry?.key || '').trim();
      const name = String(entry?.name || '').trim();
      if (!key || !name) return null;

      return {
        key,
        name,
        description: entry?.description ? String(entry.description) : undefined,
        price_cents: clampConfiguredAddonQuantity(Number(entry?.price_cents), Number.MAX_SAFE_INTEGER),
        group: entry?.group ? String(entry.group) : undefined,
        allowed_variant_keys: normalizeStringArray(entry?.allowed_variant_keys),
        requires_any_variant_keys: normalizeStringArray(entry?.requires_any_variant_keys),
        requires_any_recurring_bolt_on_keys: normalizeStringArray(entry?.requires_any_recurring_bolt_on_keys),
        conflicts_recurring_bolt_on_keys: normalizeStringArray(entry?.conflicts_recurring_bolt_on_keys),
        requirement_description: entry?.requirement_description ? String(entry.requirement_description) : undefined,
        type: entry?.type ? String(entry.type) : defaultType,
      } satisfies NamedAddonDefinition;
    })
    .filter(Boolean) as NamedAddonDefinition[];
}

export function getRecurringBoltOnDefinitions(service: MarketplaceService): NamedAddonDefinition[] {
  return getNamedAddonDefinitions(service.metadata?.recurring_bolt_ons, 'recurring_bolt_on');
}

export function getOneTimeAddonDefinitions(service: MarketplaceService): OneTimeAddonDefinition[] {
  const rawDefinitions = service.metadata?.one_time_addons;
  if (!Array.isArray(rawDefinitions)) return [];

  return rawDefinitions
    .map((entry: any) => {
      const base = getNamedAddonDefinitions([entry], 'one_time')[0];
      if (!base) return null;

      return {
        ...base,
        max_per_order: Math.max(1, clampConfiguredAddonQuantity(Number(entry?.max_per_order), Number.MAX_SAFE_INTEGER) || 1),
      } satisfies OneTimeAddonDefinition;
    })
    .filter(Boolean) as OneTimeAddonDefinition[];
}

export function getAvailableRecurringBoltOns(service: MarketplaceService, variantKey?: string): NamedAddonDefinition[] {
  return getRecurringBoltOnDefinitions(service).filter((addon) => {
    if (!addon.allowed_variant_keys?.length) return true;
    return Boolean(variantKey && addon.allowed_variant_keys.includes(variantKey));
  });
}

export function getAvailableOneTimeAddons(service: MarketplaceService, variantKey?: string): OneTimeAddonDefinition[] {
  return getOneTimeAddonDefinitions(service).filter((addon) => {
    if (!addon.allowed_variant_keys?.length) return true;
    return Boolean(variantKey && addon.allowed_variant_keys.includes(variantKey));
  });
}

export function normalizeRecurringBoltOnSelections(selections?: NamedAddonSelection[] | null): NamedAddonSelection[] {
  if (!Array.isArray(selections)) return [];

  const seen = new Set<string>();
  const normalized: NamedAddonSelection[] = [];

  selections.forEach((selection) => {
    const addonKey = String(selection?.addon_key || '').trim();
    if (!addonKey || seen.has(addonKey)) return;
    seen.add(addonKey);
    normalized.push({ addon_key: addonKey });
  });

  return normalized;
}

export function normalizeOneTimeAddonSelections(selections?: OneTimeAddonSelection[] | null): OneTimeAddonSelection[] {
  if (!Array.isArray(selections)) return [];

  const merged = new Map<string, number>();
  selections.forEach((selection) => {
    const addonKey = String(selection?.addon_key || '').trim();
    const quantity = clampConfiguredAddonQuantity(Number(selection?.quantity), Number.MAX_SAFE_INTEGER);
    if (!addonKey || quantity <= 0) return;
    merged.set(addonKey, (merged.get(addonKey) || 0) + quantity);
  });

  return Array.from(merged.entries()).map(([addon_key, quantity]) => ({ addon_key, quantity }));
}

export function isRecurringBoltOnSelectionAllowed(
  definition: NamedAddonDefinition,
  variantKey?: string,
  selections?: NamedAddonSelection[] | null,
): boolean {
  const selectedAddonKeys = new Set(
    normalizeRecurringBoltOnSelections(selections).map((selection) => selection.addon_key),
  );
  const requiredVariantKeys = definition.requires_any_variant_keys || [];
  const requiredAddonKeys = definition.requires_any_recurring_bolt_on_keys || [];
  const dependencySatisfied =
    (!requiredVariantKeys.length && !requiredAddonKeys.length) ||
    Boolean(
      (variantKey && requiredVariantKeys.includes(variantKey)) ||
      requiredAddonKeys.some((key) => selectedAddonKeys.has(key)),
    );
  const hasConflict = (definition.conflicts_recurring_bolt_on_keys || [])
    .some((key) => selectedAddonKeys.has(key));

  return dependencySatisfied && !hasConflict;
}

export function resolveRecurringBoltOns(
  service: MarketplaceService,
  variantKey?: string,
  selections?: NamedAddonSelection[] | null,
): ResolvedNamedAddonSelection[] {
  const definitions = getAvailableRecurringBoltOns(service, variantKey);
  const definitionMap = new Map(definitions.map((addon) => [addon.key, addon]));

  return normalizeRecurringBoltOnSelections(selections)
    .map((selection) => {
      const definition = definitionMap.get(selection.addon_key);
      if (!definition) return null;

      return {
        addon_key: definition.key,
        name: definition.name,
        description: definition.description,
        group: definition.group,
        type: definition.type,
        price_cents: definition.price_cents,
        total_price_cents: definition.price_cents,
      } satisfies ResolvedNamedAddonSelection;
    })
    .filter(Boolean) as ResolvedNamedAddonSelection[];
}

export function resolveOneTimeAddons(
  service: MarketplaceService,
  variantKey?: string,
  selections?: OneTimeAddonSelection[] | null,
): ResolvedOneTimeAddonSelection[] {
  const definitions = getAvailableOneTimeAddons(service, variantKey);
  const definitionMap = new Map(definitions.map((addon) => [addon.key, addon]));

  return normalizeOneTimeAddonSelections(selections)
    .map((selection) => {
      const definition = definitionMap.get(selection.addon_key);
      if (!definition) return null;
      const maxPerOrder = Math.max(1, definition.max_per_order || 1);
      const quantity = clampConfiguredAddonQuantity(selection.quantity, maxPerOrder);
      if (quantity <= 0) return null;

      return {
        addon_key: definition.key,
        name: definition.name,
        description: definition.description,
        group: definition.group,
        type: definition.type,
        quantity,
        price_cents: definition.price_cents,
        total_price_cents: definition.price_cents * quantity,
        max_per_order: maxPerOrder,
      } satisfies ResolvedOneTimeAddonSelection;
    })
    .filter(Boolean) as ResolvedOneTimeAddonSelection[];
}

export function getRecurringBoltOnsTotalCents(
  service: MarketplaceService,
  variantKey?: string,
  selections?: NamedAddonSelection[] | null,
): number {
  return resolveRecurringBoltOns(service, variantKey, selections).reduce((sum, addon) => sum + addon.total_price_cents, 0);
}

export function getOneTimeAddonsTotalCents(
  service: MarketplaceService,
  variantKey?: string,
  selections?: OneTimeAddonSelection[] | null,
): number {
  return resolveOneTimeAddons(service, variantKey, selections).reduce((sum, addon) => sum + addon.total_price_cents, 0);
}
